import { useState, useEffect, useRef } from 'react';
import { useKitbagAuth } from '@statsbomb/kitbag-auth';

/* 

{
  defaultCompetition: 
  defaultTeam:
  scoutSetups: [],
  scoutFavourites: [],
}

*/
const useAccountConfig = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const [account, setAccount] = useState();
  const { getAccessTokenSilently } = useKitbagAuth();
  const isMountedRef = useRef(false);
  const apiUrl = process.env.ACCOUNT_CONFIG;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `bearer ${token}`,
        },
      });
      if (response.status !== 200) {
        setIsLoading(false);
        setError(response.statusText);
        return;
      }
      const data = await response.json().catch((err) => {
        if (isMountedRef.current) {
          // eslint-disable-next-line no-console
          console.error(err);
          setError(err);
          setIsLoading(false);
        }
      });

      if (isMountedRef.current) {
        setIsLoading(false);
        setAccount(data);
      }
    };

    if (!account) {
      fetchData();
    }
  }, [account]);

  const fetchLatestData = async () => {
    setIsLoading(true);
    const token = await getAccessTokenSilently();
    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `bearer ${token}`,
      },
    });
    const data = await response.json();
    const etag = response.headers.get('ETag');
    setIsLoading(false);
    return { data, etag };
  };

  // update the account
  const add = async (input) => {
    try {
      const { data, etag } = await fetchLatestData();
      // clone
      const currentAccount = { ...data };
      const updatedAccount = { ...currentAccount, ...input };

      const body = JSON.stringify(updatedAccount);
      const token = await getAccessTokenSilently();
      await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          Authorization: `bearer ${token}`,
          'If-Match': etag,
        },
        body,
      }).then((res) => {
        if (!res.ok) {
          setError('Error: Data not updated.');
        } else {
          setAccount(updatedAccount); // update the local value
        }
        setIsLoading(false);
      });
    } catch (err) {
      console.error('Failed to add data with ETag:', err);
      setError(err);
    }
  };

  // remove a scout setup from the account
  const remove = async (setup) => {
    try {
      const { data, etag } = await fetchLatestData();
      const saved = data.scoutSetups.filter((set) => set.id !== setup.id);
      const updatedAccount = { ...data, scoutSetups: saved };

      const body = JSON.stringify(updatedAccount);
      const token = await getAccessTokenSilently();
      await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          Authorization: `bearer ${token}`,
          'If-Match': etag,
        },
        body,
      }).then((res) => {
        if (!res.ok) {
          setError('Error: Data not updated.');
        } else {
          setAccount(updatedAccount); // update the local value
        }
        setIsLoading(false);
      });
    } catch (err) {
      console.error('Failed to remove data with ETag:', err);
      setError(err);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // isLoading: boolean - true if the data is currently being fetched
  // error: the actual error if one occurs
  // account: the current shared account:
  // {
  //   defaultCompetition:
  //   defaultTeam:
  //   scoutSetups: [],
  //   scoutFavourites: [],
  // }
  // save: function to save partial updates to the shared account
  // saveUnsafe: function to save the entire shared account
  // add: function to add an scoutSetup
  // remove: function to remove an scoutSetup
  return { isLoading, error, account, add, remove };
};

export default useAccountConfig;
